import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { Editor } from 'grapesjs';
import { IApplicationContext } from '../types/applicationContext';
import { ReactComponentWrapper } from './ReactComponentWrapper';

// Атрибут-маркер для идентификации элемента, куда должны рендериться дочерние компоненты
// Используется для создания "слота" внутри React-компонента для дочерних элементов GrapesJS
const CHILDREN_MARKER_ATTR = 'data-gjs-children';

/**
 * Базовый адаптер для интеграции React-компонентов в GrapesJS
 * Создает новый тип компонентов 'react-component', который умеет работать с React
 *
 * @param editor - Экземпляр редактора GrapesJS
 * @param isEditor - Флаг, указывающий, что мы находимся в редакторе (а не в режиме предпросмотра)
 * @param context - Контекст приложения, который будет доступен React-компонентам
 */
export function setupReactAdapter(
  editor: Editor,
  {
    isEditor,
    context,
  }: {
    isEditor: boolean;
    context: IApplicationContext;
  },
) {
  // Получаем доступ к менеджеру компонентов GrapesJS
  const domc = editor.DomComponents;
  // Получаем стандартный тип компонента для наследования базовой функциональности
  const defaultType = domc.getType('default');
  const defaultModel = defaultType.model;

  // Регистрируем новый тип компонента 'react-component'
  domc.addType('react-component', {
    // Расширяем модель компонента (хранит данные и отвечает за экспорт)
    model: {
      /**
       * Переопределяем метод toHTML для корректного экспорта React-компонентов
       * Стандартный метод использует tagName, мы используем тип компонента
       * Это позволяет экспортировать React-компоненты с их именами вместо div
       */
      toHTML(opts = {}) {
        // Используем оригинальный метод, но заменяем тег на имя компонента
        return defaultModel.prototype.toHTML.call(this, {
          ...opts,
          tag: this.get('type'), // Используем тип компонента как имя тега
        });
      },
    },
    // Расширяем представление компонента (отвечает за рендеринг в редакторе)
    view: {
      /**
       * Определяем базовый HTML-тег для контейнера React-компонента
       * В этот div будет монтироваться React-приложение
       */
      tagName: () => 'div',

      // Корень React для этого компонента (хранит ссылку на ReactDOM корень)
      _reactRoot: null as Root | null,

      // Временный контейнер для хранения дочерних элементов до их перемещения в маркер
      _childrenContainer: null as HTMLElement | null,

      /**
       * Инициализация компонента
       * Устанавливает слушателей событий для реагирования на изменения в модели
       */
      init() {
        const { model } = this;

        // Перерендеринг при изменении атрибутов компонента
        this.listenTo(model, 'change:attributes', this.render);

        // Перерендеринг при изменении структуры дочерних компонентов
        this.listenTo(model.components(), 'add remove reset', this.handleComponentsChange);

        // Настройка событий редактирования после полного рендеринга компонента
        this.listenTo(this, 'mounted', this.setupEditableEvents);
      },

      /**
       * Устанавливает обработчики событий для редактирования содержимого маркера
       * Позволяет пользователю редактировать содержимое маркера двойным кликом
       */
      setupEditableEvents() {
        // Находим маркер дочерних элементов
        const childrenMarker = this.el.querySelector(`[${CHILDREN_MARKER_ATTR}]`) as HTMLElement | null;
        if (!childrenMarker) {
          return;
        }

        // Очищаем старые обработчики, чтобы избежать дублирования
        if (this._markerDblClickHandler) {
          childrenMarker.removeEventListener('dblclick', this._markerDblClickHandler);
          childrenMarker.removeEventListener('blur', this._markerBlurHandler);
        }

        // Обработчик двойного клика: включает редактирование и сохраняет оригинальное содержимое
        this._markerDblClickHandler = (e: MouseEvent) => {
          e.stopPropagation(); // Предотвращаем всплытие события для избежания конфликтов
          childrenMarker.contentEditable = 'true'; // Включаем редактирование
          childrenMarker.dataset.originalContent = childrenMarker.innerHTML; // Сохраняем исходное содержимое
          childrenMarker.focus(); // Устанавливаем фокус для начала редактирования
        };

        // Обработчик потери фокуса: сохраняет изменения в модель
        this._markerBlurHandler = () => {
          childrenMarker.contentEditable = 'false'; // Выключаем редактирование

          // Проверяем, изменилось ли содержимое
          const originalContent = childrenMarker.dataset.originalContent;
          if (originalContent !== childrenMarker.innerHTML) {
            // Обновляем модель новым содержимым
            this.model.components(childrenMarker.innerHTML);
          }
        };

        // Добавляем обработчики к маркеру
        childrenMarker.addEventListener('dblclick', this._markerDblClickHandler);
        childrenMarker.addEventListener('blur', this._markerBlurHandler);
      },

      /**
       * Обрабатывает изменения дочерних компонентов
       * Использует таймер для предотвращения множественных рендерингов при массовых изменениях
       */
      handleComponentsChange() {
        // Очищаем предыдущий таймер для избежания лишних рендеров
        clearTimeout(this._componentChangeTimeout);
        // Устанавливаем новый таймер для отложенного рендеринга
        this._componentChangeTimeout = setTimeout(() => this.render(), 0);
      },

      /**
       * Создает временный скрытый контейнер для хранения дочерних элементов
       * до их перемещения в маркер
       */
      getChildrenContainerHolder() {
        // Создаем контейнер, если он еще не существует
        if (!this._childrenContainer) {
          this._childrenContainer = document.createElement('div');
          this._childrenContainer.style.display = 'none'; // Скрываем контейнер
        }

        return this._childrenContainer;
      },

      /**
       * Возвращает элемент, в который должны быть помещены дочерние компоненты
       */
      getChildrenContainer() {
        // Ищем маркер дочерних элементов в DOM
        const childrenMarker = this.el.querySelector(`[${CHILDREN_MARKER_ATTR}]`);

        // Возвращаем маркер, если он найден, иначе временный контейнер
        return childrenMarker || this.getChildrenContainerHolder();
      },

      /**
       * Создает React-элемент маркера для дочерних элементов
       * Этот маркер будет рендериться внутри React-компонента
       */
      createChildrenMarker() {
        return React.createElement('span', {
          [CHILDREN_MARKER_ATTR]: true, // Атрибут-маркер для идентификации
          contentEditable: 'false', // По умолчанию не редактируемый
          style: { display: 'inherit' }, // Наследуем стиль отображения родителя
        });
      },

      /**
       * Создает React-элемент для компонента с провайдерами и маркером дочерних элементов
       * Этот элемент будет рендериться в DOM
       */
      createReactElement() {
        const { model } = this;
        const Component = model.get('component');
        const props = { ...model.get('attributes') };

        return React.createElement(
          ReactComponentWrapper,
          {
            isEditor,
            context,
            component: Component,
            props,
          },
          this.createChildrenMarker(),
        );
      },

      /**
       * Рендерит React-компонент в DOM-элемент
       * Использует React 18 API с createRoot
       */
      renderReactComponent() {
        // Получаем DOM-элемент, в который будет рендериться React
        const { el } = this;

        // Создаем корень React, если его еще нет
        if (!this._reactRoot) {
          this._reactRoot = createRoot(el);
        }

        // Рендерим React-элемент в корень
        this._reactRoot.render(this.createReactElement());
      },

      /**
       * Перемещает дочерние элементы из временного контейнера в маркер
       * Это ключевой метод для интеграции GrapesJS компонентов внутрь React-компонента
       */
      attachChildren() {
        const { el } = this;
        // Находим маркер дочерних элементов
        const childrenMarker = el.querySelector(`[${CHILDREN_MARKER_ATTR}]`);

        // Если маркер найден, перемещаем в него дочерние элементы
        if (childrenMarker) {
          const tmpContainer = this.getChildrenContainerHolder();

          // Переносим все дочерние элементы из временного контейнера в маркер
          while (tmpContainer.firstChild) {
            childrenMarker.appendChild(tmpContainer.firstChild);
          }
        }
      },

      /**
       * Основной метод рендеринга, вызываемый GrapesJS
       * Переопределяет стандартный процесс для поддержки React-компонентов
       */
      render() {
        // Обновляем атрибуты элемента (стандартное поведение GrapesJS)
        this.updateAttributes();

        // СНАЧАЛА рендерим React-компонент, чтобы создать маркер в DOM
        this.renderReactComponent();

        // Затем делаем микрозадержку, чтобы React успел обновить DOM
        // и создать маркер для дочерних элементов
        const checkMarkerAndRenderChildren = () => {
          // Проверяем, появился ли маркер в DOM
          const childrenMarker = this.el.querySelector(`[${CHILDREN_MARKER_ATTR}]`);

          if (childrenMarker) {
            // Маркер найден, можно рендерить дочерние компоненты GrapesJS
            this.renderChildren();
            // Перемещаем дочерние элементы из временного контейнера в маркер
            this.attachChildren();

            // Триггерим событие mounted для настройки событий редактирования
            this.trigger('mounted');
          } else {
            // Маркер еще не создан, пробуем еще раз в следующем кадре анимации
            requestAnimationFrame(checkMarkerAndRenderChildren);
          }
        };

        // Запускаем проверку наличия маркера
        checkMarkerAndRenderChildren();

        return this;
      },

      /**
       * Очистка ресурсов при удалении компонента
       * Предотвращает утечки памяти и ресурсов
       */
      remove() {
        // Очищаем таймер изменения компонентов
        clearTimeout(this._componentChangeTimeout);

        // Размонтируем React-компонент если он есть
        if (this._reactRoot) {
          this._reactRoot.unmount();
          this._reactRoot = null;
        }

        // Вызываем базовый метод remove для стандартной очистки GrapesJS
        return defaultType.view.prototype.remove.apply(this);
      },
    },
  });
}
